import { LAYOUT } from '../../../ui/layout';

/*
 * seat-layout.ts — the single source of truth for where every seat, hand and
 * played card goes, for ANY player count (4/6/8...).
 *
 * Seats are placed around the LAYOUT ellipse. The local player is index 0 and
 * pins to bottom-center; the rest distribute evenly. Because the server view
 * model is already caller-relative (tablePlayers[0] === local), no seat
 * remapping is needed.
 */

export interface SeatDescriptor {
  /** Seat index; 0 is always the local player. Matches tablePlayers[index]. */
  index: number;
  isLocal: boolean;
  /** Angle around the table center, degrees, y-down screen coords. */
  angleDeg: number;
  /**
   * Team by seat position. Only a fallback for an empty seat, which exists only
   * pre-game — where the view isn't rotated, so position and server team order
   * agree. A seated player's team comes from the view model (TablePlayerModelV2),
   * which is authoritative once the caller-relative rotation kicks in.
   */
  teamIndex: number;
  /** Avatar center, in design px. */
  avatar: { x: number; y: number };
  /** Fan anchor + facing. rotationDeg 0 = cards upright (bottom seat). */
  hand: { x: number; y: number; rotationDeg: number };
  /** Where this seat's played (trick) card lands, in design px. */
  trick: { x: number; y: number };
}

const toRadians = (deg: number) => (deg * Math.PI) / 180;

/**
 * Compute seat descriptors for `n` players.
 * θ_i = 90 + i·(360/n): i=0 → 90° → bottom-center. For n=4 this reproduces the
 * legacy bottom/left/top/right positions.
 */
export function computeSeats(n: number): SeatDescriptor[] {
  const { table, seat, trick, fan } = LAYOUT;
  const teams = Math.max(1, Math.floor(n / 2));

  return Array.from({ length: n }, (_, i) => {
    const angleDeg = 90 + i * (360 / n);
    const cos = Math.cos(toRadians(angleDeg));
    const sin = Math.sin(toRadians(angleDeg));

    return {
      index: i,
      isLocal: i === 0,
      angleDeg,
      teamIndex: i % teams,
      avatar: {
        x: table.cx + seat.radiusX * cos,
        y: table.cy + seat.radiusY * sin,
      },
      hand: {
        x: table.cx + seat.radiusX * fan.insetX * cos,
        y: table.cy + seat.radiusY * fan.insetY * sin,
        rotationDeg: angleDeg - 90,
      },
      trick: {
        x: table.cx + trick.radiusX * cos,
        y: table.cy + trick.radiusY * sin,
      },
    };
  });
}
